// Purge → revise → re-send for a Change Order that is already staged / imported in Foundation.
// Admin / Accounting only: confirms the purge in Foundation, the bridge (2.6.2) clears the stale staging
// rows, the CO goes back to draft under the same number. Every step is logged on the Work Order.
import { useState } from "react";
import { Loader2, Unlock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { unlockChangeOrder } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

export const UNLOCK_ROLES = ["admin", "accounting", "office"];
export function useCanUnlockSync() {
  const { user } = useAuth();
  return UNLOCK_ROLES.includes(String(user?.role || "").toLowerCase());
}

export function COUnlockDialog({ woId, woNumber, co, onClose, onUnlocked }) {
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fs = co.foundation_sync || {};
  const fdnNo = fs.foundation_change_order_no || "?";
  const run = async () => {
    setBusy(true); setError(null);
    try {
      const r = await unlockChangeOrder(woId, co.id);
      toast.success(r.data?.message || "Unlocked — the change order is back in draft", { duration: 8000 });
      onUnlocked?.(r.data);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not unlock — nothing was changed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid={`co-unlock-dialog-${co.id}`}>
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-1.5"><Unlock className="w-4 h-4 text-amber-600" /> Unlock {co.co_number} to revise it</DialogTitle>
          <DialogDescription className="text-xs">
            This change order is <b>{fs.label || fs.status}</b> (Foundation change order <span className="font-mono">{fdnNo}</span> on job <span className="font-mono">{woNumber}</span>).
            Once unlocked it goes back to draft, can be edited, and is sent again under the same number after re-approval.
          </DialogDescription>
        </DialogHeader>
        <ol className="text-xs space-y-1.5 list-decimal pl-4 text-slate-700 dark:text-slate-200" data-testid={`co-unlock-steps-${co.id}`}>
          <li>In Foundation: Job Costing › Enter J/C Change Orders → job <span className="font-mono">{woNumber}</span> → delete change order <span className="font-mono">{fdnNo}</span>
            (and anything it posted). If it was never imported, delete it from the Change Orders Sync report instead.</li>
          <li>Tick the confirmation below. ServiceOps then removes the stale sync-staging rows through the bridge so the re-send is not rejected as "already staged".</li>
          <li>Revise the change order here, re-approve it, and accounting sends it again from Settings › Integrations › Foundation › Sync.</li>
        </ol>
        <label className="flex items-start gap-2 text-xs rounded border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-2 cursor-pointer">
          <input type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} className="mt-0.5" data-testid={`co-unlock-confirm-${co.id}`} />
          <span>I have purged change order <span className="font-mono">{fdnNo}</span> from job <span className="font-mono">{woNumber}</span> in Foundation.</span>
        </label>
        {error && <div className="text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded p-2 whitespace-pre-wrap" data-testid={`co-unlock-error-${co.id}`}>{error}</div>}
        <div className="flex items-center gap-2">
          <button type="button" onClick={run} disabled={!ticked || busy}
            className="text-xs px-2.5 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1" data-testid={`co-unlock-run-${co.id}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlock className="w-3 h-3" />} Clear staging rows &amp; unlock
          </button>
          <button type="button" onClick={onClose} disabled={busy} className="ml-auto text-xs px-2.5 py-1 rounded border border-slate-300 dark:border-slate-600" data-testid={`co-unlock-cancel-${co.id}`}>Cancel</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default COUnlockDialog;
